// AppShell.jsx
import React, { useContext, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { LogOut, Menu, Grid, User } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AppShell({ children }) {
  const { user, logout } = useContext(AuthContext);
  const [showAppPopup, setShowAppPopup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const recentApps = [
    { name: 'Smart Doc Assistant', route: 'smart-doc', icon: <Grid className="w-4 h-4" /> },
    { name: 'VisionGuard', route: 'vision-guard', icon: <Grid className="w-4 h-4" /> },
    { name: 'AI Content Creator', route: 'content-creator', icon: <Grid className="w-4 h-4" /> },
  ];

  const isContentCreator = location.pathname.startsWith('/app/content-creator');
  const isLeadCollector = location.pathname.startsWith('/app/lead-collector');
  const isReplyBot = location.pathname.startsWith('/app/replybot');
  
  
  const handleLogout = () => {
    logout(); // clear context
    localStorage.removeItem('token'); // or sessionStorage if used
    navigate('/login'); // redirect
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Sticky Top Bar */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-2">
          <Link to="/dashboard" title="Go to Dashboard">
            <img src="/ennoverse.png" alt="Ennoverse" className="h-8" />
          </Link>
        </div>

        <div className="flex items-center gap-4">
          {/* Apps button - hidden on small screens */}
          <div title="Available Apps" className="hidden sm:block">
            <button
              onClick={() => setShowAppPopup(!showAppPopup)}
              className="text-gray-600 hover:text-black"
            >
              <Grid className="w-5 h-5" />
            </button>
          </div>

          {showAppPopup && (
            <div className="absolute top-14 right-4 bg-white border shadow rounded-md w-72 z-50 p-3 space-y-2">
              <p className="text-sm text-gray-500 font-semibold mb-2">Recently Used Apps</p>
              {recentApps.map((app) => (
                <div
                  key={app.route}
                  onClick={() => {
                    navigate(`/app/${app.route}`);
                    setShowAppPopup(false);
                  }}
                  className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100 cursor-pointer"
                >
                  {app.icon}
                  <span className="text-sm font-medium">{app.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* User info - hidden on small screens */}
          <div className="hidden sm:flex items-center gap-2 text-sm text-gray-600" title="Logged in user">
            <User className="w-4 h-4" />
            <span>{user?.name || user?.email}</span>
          </div>

          <div title="Logout">
            <button
              onClick={handleLogout}
              className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* App-specific sub-navigation for AI Content Creator */}
      {isContentCreator && (
        <nav className="bg-gray-100 border-b px-6 py-2 text-sm font-medium flex gap-6">
          <Link
            to="/app/content-creator"
            className={location.pathname === '/app/content-creator' ? 'text-primary font-semibold' : 'text-gray-500 hover:text-black'}
          >
            ✍️ {t('create')}
          </Link>
          <Link
            to="/app/content-creator/my-creations"
            className={location.pathname === '/app/content-creator/my-creations' ? 'text-primary font-semibold' : 'text-gray-500 hover:text-black'}
          >
            📁 {t('myCreations')}
          </Link>
        </nav>
      )}

{isLeadCollector && (
        <nav className="bg-gray-100 border-b px-6 py-2 text-sm font-medium flex gap-6">
          <Link
            to="/app/lead-collector"
            className={location.pathname === '/app/lead-creator' ? 'text-primary font-semibold' : 'text-gray-500 hover:text-black'}
          >
            ✍️ Capture Lead
          </Link>
          <Link
            to="/app/lead-collector/leads"
            className={location.pathname === '/app/lead-collector/leads' ? 'text-primary font-semibold' : 'text-gray-500 hover:text-black'}
          >
            📁 My Leads
          </Link>
        </nav>
      )}

{isReplyBot && (
  <nav className="bg-gray-100 border-b px-6 py-2 text-sm font-medium flex gap-6">
    <Link
      to="/app/replybot/inbox"
      className={location.pathname === '/app/replybot/inbox' ? 'text-primary font-semibold' : 'text-gray-500 hover:text-black'}
    >
      💬 Inbox
    </Link>
    <Link
      to="/app/replybot/settings"
      className={location.pathname === '/app/replybot/settings' ? 'text-primary font-semibold' : 'text-gray-500 hover:text-black'}
    >
      ⚙️ Settings
    </Link>
    <Link
      to="/app/replybot/templates"
      className={location.pathname === '/app/replybot/templates' ? 'text-primary font-semibold' : 'text-gray-500 hover:text-black'}
    >
      ⚙️ Templates
    </Link>
  </nav>
)}


      <main className="flex-1 bg-gray-50 p-3">
        <div className="mx-0 w-full grid grid-cols-1 lg:grid-cols-12 gap-6">
          {children}
        </div>
      </main>
    </div>
  );
}
